import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Spinner } from "@air/primitive-spinner";
import { tailwindMerge } from "@air/tailwind-variants";
import { memo, useEffect, useRef, useState } from "react";
import { PDFViewerOutlinePageMenu } from "./PDFViewerOutlinePageMenu";
import { getImageFromCanvas } from "./utils/getImageFromCanvas";
import { getOutlineImageCanvasContext } from "./utils/getOutlineImageCanvasContext";
export const PDF_OUTLINE_PAGE_THUMBNAIL_HEIGHT = 92;
const DRAW_UPSCALE_FACTOR = 1;
export const PDFViewerOutlinePage = /*#__PURE__*/ memo((param)=>{
    let { pageNumber, isSelected, onPageClick, pdfPageCacheRef, thumbnailCacheRef, renderOutlinePageMenu } = param;
    const imageContainerRef = useRef(null);
    const renderPromiseRef = useRef();
    const [imageData, setImageData] = useState();
    const [imageLoadingState, setImageLoadingState] = useState("loading");
    const isMounted = useRef(false);
    useEffect(()=>{
        isMounted.current = true;
        return ()=>{
            isMounted.current = false;
        };
    }, []);
    useEffect(()=>{
        var _thumbnailCacheRef_current;
        const loadPageImage = async ()=>{
            var _pdfPageCacheRef_current;
            const pdfPage = await ((_pdfPageCacheRef_current = pdfPageCacheRef.current) === null || _pdfPageCacheRef_current === void 0 ? void 0 : _pdfPageCacheRef_current.getPage(pageNumber));
            if (!pdfPage) return;
            const viewport = pdfPage.getViewport({
                scale: 1
            });
            const ratio = viewport.width / viewport.height;
            const imgHeight = PDF_OUTLINE_PAGE_THUMBNAIL_HEIGHT;
            const imgWidth = PDF_OUTLINE_PAGE_THUMBNAIL_HEIGHT * ratio;
            const scale = imgWidth / viewport.width;
            const drawViewport = pdfPage.getViewport({
                scale: scale * DRAW_UPSCALE_FACTOR
            });
            // Render the thumbnail at a larger size and downsize the canvas (similar
            // to `setImage`), to improve consistency between thumbnails created by
            // the `draw` and `setImage` methods (fixes issue 8233).
            // NOTE: To primarily avoid increasing memory usage too much, but also to
            //   reduce downsizing overhead, we purposely limit the up-scaling factor.
            const { ctx, transform, canvas } = getOutlineImageCanvasContext({
                upscaleFactor: DRAW_UPSCALE_FACTOR,
                width: imgWidth,
                height: imgHeight,
                enableHMA: false
            });
            if (!ctx) return;
            renderPromiseRef.current = pdfPage.render({
                canvasContext: ctx,
                transform,
                viewport: drawViewport
            });
            renderPromiseRef.current.promise.then(()=>{
                renderPromiseRef.current = undefined;
                if (!isMounted.current) return;
                const imageData = getImageFromCanvas({
                    canvas,
                    width: imgWidth,
                    height: imgHeight
                });
                if (imageData) {
                    var _thumbnailCacheRef_current;
                    setImageLoadingState("loaded");
                    setImageData(imageData);
                    (_thumbnailCacheRef_current = thumbnailCacheRef.current) === null || _thumbnailCacheRef_current === void 0 ? void 0 : _thumbnailCacheRef_current.setThumbnail(pageNumber, imageData);
                } else {
                    setImageLoadingState("error");
                }
            }).catch(()=>{
                if (!isMounted.current) return;
                setImageLoadingState("error");
            }).finally(()=>{
                // Zeroing the width and height causes Firefox to release graphics
                // resources immediately, which can greatly reduce memory consumption.
                canvas.width = 0;
                canvas.height = 0;
            });
        };
        const thumbnail = (_thumbnailCacheRef_current = thumbnailCacheRef.current) === null || _thumbnailCacheRef_current === void 0 ? void 0 : _thumbnailCacheRef_current.getThumbnail(pageNumber);
        if (thumbnail) {
            setImageData(thumbnail);
            setImageLoadingState("loaded");
        } else {
            loadPageImage();
        }
        return ()=>{
            var _renderPromiseRef_current;
            (_renderPromiseRef_current = renderPromiseRef.current) === null || _renderPromiseRef_current === void 0 ? void 0 : _renderPromiseRef_current.cancel();
        };
    }, [
        pageNumber,
        pdfPageCacheRef,
        thumbnailCacheRef
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": "PDF_VIEWER_OUTLINE_PAGE",
        "data-page-number": pageNumber,
        "data-isselected": isSelected,
        className: "group/outline-page relative flex size-full",
        children: [
            /*#__PURE__*/ _jsxs("button", {
                onClick: ()=>onPageClick(pageNumber),
                className: tailwindMerge("flex h-full w-full flex-col items-center justify-center rounded bg-grey-1 px-0.5 pt-0.5", isSelected ? "bg-grey-5" : "hover:bg-grey-6"),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        ref: imageContainerRef,
                        className: "flex size-full items-center justify-center rounded bg-black",
                        children: [
                            imageLoadingState === "loading" && /*#__PURE__*/ _jsx(Spinner, {
                                className: "size-4 text-teal-9"
                            }),
                            imageLoadingState === "loaded" && /*#__PURE__*/ _jsx("img", {
                                src: imageData,
                                className: "pdfThumbnailImage bg-black object-contain",
                                alt: "Thumbnail of page ".concat(pageNumber),
                                style: {
                                    height: PDF_OUTLINE_PAGE_THUMBNAIL_HEIGHT
                                }
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "py-2 text-12 font-bold text-grey-12",
                        children: [
                            "Page ",
                            pageNumber
                        ]
                    })
                ]
            }),
            !!renderOutlinePageMenu && /*#__PURE__*/ _jsx(PDFViewerOutlinePageMenu, {
                pageNumber: pageNumber,
                renderOutlinePageMenu: renderOutlinePageMenu
            })
        ]
    });
});
PDFViewerOutlinePage.displayName = "PDFViewerOutlinePage";
